<script setup>
import { computed, onMounted, ref, defineEmits } from "vue"

import Lottie from "@/components/widgets/lottie.vue"
import animationData from "@/components/widgets/msoeawqm.json"
import * as persianTools from "@persian-tools/persian-tools"
import PersianDate from "persian-date"

import store from "../../state/store"
import useOperators from "../../use/useOperators"
import useDebouncedFunction from "../../use/useDebouncedFunction"

const operators = computed(() => store.state.operators)

const {
  operatorStatus,
  search,
  perPage,
  sortField,
  sortDirection,
  getOperators,
  sortOperators,
} = useOperators()
const { handleInput } = useDebouncedFunction(getOperators)

import TableHeaderCell from "@/components/table/TableHeaderCell.vue"

const emit = defineEmits(["clickEdit", "clickChangePass"])

onMounted(() => {
  getOperators()
})

const toPersianDateTime = (date) => {
  const [datePart, timePart] = date.split(" ")
  const [year, month, day] = datePart.split("-")
  const [hour, minute, second] = timePart.split(":")
  const gregorianDate = new Date(year, month - 1, day, hour, minute, second)
  const persianDate = new PersianDate(gregorianDate)
  return persianDate.format("YYYY/MM/DD HH:mm:ss")
}

function getForPage(ev, link) {
  ev.preventDefault()
  if (!link.url || link.active) {
    return
  }

  getOperators(link.url)
}

const defaultOptions = ref({
  animationData: animationData,
})

const editOperator = (ope) => {
  emit("clickEdit", ope)
}

const changePasswordOperator = (opeId) => {
  emit("clickChangePass", opeId)
}
</script>

<template>
  <b-card-body class="border-bottom-dashed border-bottom">
    <b-row class="g-3">
      <b-col xl="7">
        <div class="search-box">
          <input
            v-model="search"
            @input="handleInput"
            type="text"
            class="form-control search"
            placeholder="جستجوی اپراتور، نام، نام خانوادگی، ایمیل و..."
          />
          <i class="ri-search-line search-icon"></i>
        </div>
      </b-col>

      <b-col xl="5">
        <b-row class="g-3">
          <b-col sm="6">
            <select
              @change="getOperators(null)"
              v-model="operatorStatus"
              class="form-select mb-3"
              aria-label="Default select example"
            >
              <option value="" selected>انتخاب وضعیت اپراتور</option>
              <option value="0">غیرفعال</option>
              <option value="1">فعال</option>
            </select>
          </b-col>

          <b-col sm="6">
            <select
              @change="getOperators(null)"
              v-model="perPage"
              class="form-select mb-3"
              aria-label="Default select example"
            >
              <option value="" selected>نمایش تعداد ردیف</option>
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="50">50</option>
            </select>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </b-card-body>
  <b-card-body>
    <div>
      <div class="table-responsive table-card mb-1">
        <table class="table align-middle" id="operatorTable">
          <thead class="table-light text-muted">
            <tr>
              <TableHeaderCell
                field="first_name"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortOperators('first_name')"
              >
                نام
              </TableHeaderCell>
              <TableHeaderCell
                field="last_name"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortOperators('last_name')"
              >
                نام خانوادگی
              </TableHeaderCell>
              <TableHeaderCell
                field="email"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortOperators('email')"
              >
                ایمیل
              </TableHeaderCell>
              <TableHeaderCell
                field="phone_number"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortOperators('phone_number')"
              >
                تلفن
              </TableHeaderCell>
              <TableHeaderCell
                field="created_at"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortOperators('created_at')"
              >
                تاریخ ثبت
              </TableHeaderCell>
              <TableHeaderCell
                field="status"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortOperators('status')"
              >
                وضعیت
              </TableHeaderCell>
              <th class="sort" data-sort="last_login">آخرین ورود</th>
              <th class="sort" data-sort="action">عملیات</th>
            </tr>
          </thead>
          <tbody
            class="list"
            v-if="operators.loading || !operators.data.length"
          >
            <tr>
              <td colspan="8">
                <div class="text-center">
                  <b-spinner
                    v-if="operators.loading"
                    variant="dark"
                    style="width: 3rem; height: 3rem"
                  ></b-spinner>
                  <div v-else>
                    <lottie
                      class="avatar-xl"
                      colors="primary:#121331,secondary:#08a88a"
                      :options="defaultOptions"
                      :height="75"
                      :width="75"
                    />
                    <h5 class="mt-2">هیچ اپراتوری وجود ندارد!</h5>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>

          <tbody class="list" v-else>
            <tr v-for="(operator, index) of operators.data" :key="index">
              <td class="operator_name">{{ operator.name }}</td>
              <td class="operator_surname">{{ operator.surname }}</td>
              <td class="email">{{ operator.email }}</td>
              <td class="phone">
                {{ persianTools.digitsEnToFa(operator.phone) }}
              </td>
              <td class="date">{{ toPersianDateTime(operator.created_at) }}</td>
              <td class="status">
                <span
                  class="badge text-uppercase"
                  :class="{
                    'badge-soft-success': operator.status == 1,
                    'badge-soft-danger': operator.status == 0,
                  }"
                  >{{ operator.status ? "فعال" : "غیرفعال" }}</span
                >
              </td>
              <td class="last-login">
                {{
                  operator.last_login_at
                    ? toPersianDateTime(operator.last_login_at)
                    : ""
                }}
              </td>
              <td>
                <ul class="list-inline hstack gap-2 mb-0">
                  <li
                    class="list-inline-item change-password"
                    data-bs-toggle="tooltip"
                    data-bs-trigger="hover"
                    data-bs-placement="top"
                    title="تغییر کلمه عبور"
                  >
                    <b-link
                      variant="text-warning"
                      class="d-inline-block change-password-btn"
                      @click="changePasswordOperator(operator)"
                    >
                      <i class="ri-lock-password-fill fs-16"></i>
                    </b-link>
                  </li>

                  <li
                    class="list-inline-item edit"
                    data-bs-toggle="tooltip"
                    data-bs-trigger="hover"
                    data-bs-placement="top"
                    title="ویرایش"
                  >
                    <b-link
                      variant="text-primary"
                      class="d-inline-block edit-item-btn"
                      @click="editOperator(operator)"
                    >
                      <i class="ri-pencil-fill fs-16"></i>
                    </b-link>
                  </li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        class="d-flex justify-content-end"
        v-if="operators.total > operators.limit"
      >
        <div class="pagination-wrap hstack gap-2" style="display: flex">
          <ul class="pagination listjs-pagination mb-0">
            <li
              v-for="(link, i) of operators.links"
              :key="i"
              class="page-item"
              :class="{
                disabled: !link.url,
                active: link.active || !link.url,
              }"
            >
              <span
                class="page-link"
                v-if="link.active"
                v-html="link.label"
              ></span>

              <a
                v-else
                href="#"
                @click="getForPage($event, link)"
                class="page-link"
                v-html="link.label"
              >
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </b-card-body>
</template>
